import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { SpecialAssistanceRequestType } from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { MListData, PregnantMasterData } from '@common/interfaces';
import {
  SpecialAssistanceRequestData,
  SpecialAssistanceRequestFoldingTypeData,
  SpecialAssistanceRequestPregnantData,
  SpecialAssistanceRequestSpecialAssistanceData,
  SpecialAssistanceRequestWalkingAbilityData,
  SpecialAssistanceRequestWheelchairTypeData,
  SpecialAssistanceRequestWheelchairsData,
} from '@common/interfaces/mybooking/modals/passenger-information-input/special-assistance-request-modal.interface';
import { PassengerInformationInputModalCommonService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal.common.service';
import { SpecialAssistanceRequestModalService } from '@common/services/my-booking/passenger-information-input-modal/special-assistance-request-modal.service';
import { SupportModalBlockComponent } from '@lib/components/support-class';
import { AlertMessageItem, AlertType } from '@lib/interfaces';
import { CommonLibService } from '@lib/services/common-lib/common-lib.service';
import { UpdateBasicInformationSupportRequest } from 'src/sdk-servicing';
import { SpecialAssistanceRequestModalFoldingTypeComponent } from './sub-components/special-assistance-request-modal-folding-type/special-assistance-request-modal-folding-type.component';
import { SpecialAssistanceRequestModalPregnantComponent } from './sub-components/special-assistance-request-modal-pregnant/special-assistance-request-modal-pregnant.component';
import { SpecialAssistanceRequestModalSpecialAssistanceComponent } from './sub-components/special-assistance-request-modal-special-assistance/special-assistance-request-modal-special-assistance.component';
import { SpecialAssistanceRequestModalWalkingAbilityComponent } from './sub-components/special-assistance-request-modal-walking-ability/special-assistance-request-modal-walking-ability.component';
import { SpecialAssistanceRequestModalWheelchairTypeComponent } from './sub-components/special-assistance-request-modal-wheelchair-type/special-assistance-request-modal-wheelchair-type.component';
import { SpecialAssistanceRequestModalWheelchairsComponent } from './sub-components/special-assistance-request-modal-wheelchairs/special-assistance-request-modal-wheelchairs.component';

/**
 * サポート情報入力モーダル
 */
@Component({
  selector: 'asw-special-assistance-request-modal',
  templateUrl: './special-assistance-request-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpecialAssistanceRequestModalComponent extends SupportModalBlockComponent {
  public type: SpecialAssistanceRequestType = SpecialAssistanceRequestType.SPECIAL_ASSISTANCE;
  public walkingAbilityMaster: Array<MListData> = [];
  public wheelchairTypeMaster: Array<MListData> = [];
  public pregnantMaster!: PregnantMasterData;

  public specialAssistance?: SpecialAssistanceRequestSpecialAssistanceData =
    this._service.specialAssistanceRequestData?.specialAssistance;
  public walkingAbility?: SpecialAssistanceRequestWalkingAbilityData =
    this._service.specialAssistanceRequestData?.walkingAbility;
  public wheelchairs?: SpecialAssistanceRequestWheelchairsData =
    this._service.specialAssistanceRequestData?.wheelchairs;
  public foldingType?: SpecialAssistanceRequestFoldingTypeData =
    this._service.specialAssistanceRequestData?.foldingType;
  public wheelchairType?: SpecialAssistanceRequestWheelchairTypeData =
    this._service.specialAssistanceRequestData?.wheelchairType;
  public pregnant?: SpecialAssistanceRequestPregnantData = this._service.specialAssistanceRequestData?.pregnant;

  public previousGroup!: SpecialAssistanceRequestType;
  public nextGroup!: SpecialAssistanceRequestType;
  public isDomesticFlight!: boolean;
  public isInfantExisting!: boolean;
  public isUSPos: boolean = false;
  public isError: boolean = false;
  public alertMessageItem!: AlertMessageItem;

  @ViewChild('specialAssistanceCom') specialAssistanceCom?: SpecialAssistanceRequestModalSpecialAssistanceComponent;
  @ViewChild('walkingAbilityCom') walkingAbilityCom?: SpecialAssistanceRequestModalWalkingAbilityComponent;
  @ViewChild('wheelchairsCom') wheelchairsCom?: SpecialAssistanceRequestModalWheelchairsComponent;
  @ViewChild('foldingTypeCom') foldingTypeCom?: SpecialAssistanceRequestModalFoldingTypeComponent;
  @ViewChild('wheelchairTypeCom') wheelchairTypeCom?: SpecialAssistanceRequestModalWheelchairTypeComponent;
  @ViewChild('pregnantCom') pregnantCom?: SpecialAssistanceRequestModalPregnantComponent;

  constructor(
    private _common: CommonLibService,
    private _change: ChangeDetectorRef,
    private _router: Router,
    private _service: SpecialAssistanceRequestModalService,
    private _s04CommonSvc: PassengerInformationInputModalCommonService
  ) {
    super(_common);
    this.closeWithUrlChange(this._router);
    this.isUSPos = _common.aswContextStoreService.aswContextData.posCountryCode === 'US';
  }

  /**
   * 初期表示処理
   */
  init() {
    this.isDomesticFlight = this.payload.isDomesticFlight;
    this.isInfantExisting = this.payload.isInfantExisting;
    this.wheelchairTypeMaster =
      this._s04CommonSvc.masterData.listDataAll.filter(
        (data) => data.data_code === 'PD_960' && data.lang === this._s04CommonSvc.lang
      ) ?? [];
    this.walkingAbilityMaster =
      this._s04CommonSvc.masterData.listDataAll.filter(
        (data) => data.data_code === 'PD_006' && data.lang === this._s04CommonSvc.lang
      ) ?? [];
    this.pregnantMaster = {
      pd020:
        this._s04CommonSvc.masterData.listDataAll.filter(
          (data) => data.data_code === 'PD_020' && data.lang === this._s04CommonSvc.lang
        ) ?? [],
      countries: this._s04CommonSvc.masterData.i18nCountries ?? [],
      phoneCountries: this._s04CommonSvc.phoneNumberCountryList ?? [],
    };
  }

  /**
   * 次へボタン表示判定
   * @returns 表示可否
   */
  isNext(): boolean | undefined {
    this.resize();
    this.specialAssistanceCom?.apply();
    switch (this.type) {
      case SpecialAssistanceRequestType.SPECIAL_ASSISTANCE:
        this.updateGroupsForSpecialAssistance();
        return this.specialAssistance?.degreeOfWalking || this.specialAssistance?.pregnant;
      case SpecialAssistanceRequestType.WALKING_ABILITY:
        this.updateGroupsForWalkingAbility();
        return true;
      case SpecialAssistanceRequestType.WHEELCHAIRS:
        this.updateGroupsForWheelchairs();
        return this.wheelchairs?.willBeCheckedIn || this.specialAssistance?.pregnant;
      case SpecialAssistanceRequestType.FOLDING_TYPE:
        this.updateGroupsForFoldingType();
        return true;
      case SpecialAssistanceRequestType.WHEELCHAIR_TYPE:
        this.updateGroupsForWheelchairType();
        return this.specialAssistance?.pregnant;
      case SpecialAssistanceRequestType.PREGNANT:
        this.updateGroupsForPregnant();
        return false;
      default:
        return false;
    }
  }

  updateGroupsForSpecialAssistance(): void {
    this.previousGroup = this.type;

    if (this.specialAssistance?.degreeOfWalking) {
      this.nextGroup = SpecialAssistanceRequestType.WALKING_ABILITY;
      return;
    }

    this.nextGroup = this.specialAssistance?.pregnant ? SpecialAssistanceRequestType.PREGNANT : this.type;
  }

  updateGroupsForWalkingAbility(): void {
    this.previousGroup = SpecialAssistanceRequestType.SPECIAL_ASSISTANCE;
    this.nextGroup = SpecialAssistanceRequestType.WHEELCHAIRS;
  }

  updateGroupsForWheelchairs(): void {
    this.previousGroup = SpecialAssistanceRequestType.WALKING_ABILITY;

    if (this.wheelchairs?.willBeCheckedIn) {
      this.nextGroup = SpecialAssistanceRequestType.FOLDING_TYPE;
      return;
    }

    this.nextGroup = this.specialAssistance?.pregnant ? SpecialAssistanceRequestType.PREGNANT : this.type;
  }

  updateGroupsForFoldingType(): void {
    this.previousGroup = SpecialAssistanceRequestType.WHEELCHAIRS;
    this.nextGroup = SpecialAssistanceRequestType.WHEELCHAIR_TYPE;
  }

  updateGroupsForWheelchairType(): void {
    this.previousGroup = SpecialAssistanceRequestType.FOLDING_TYPE;
    this.nextGroup = this.specialAssistance?.pregnant ? SpecialAssistanceRequestType.PREGNANT : this.type;
  }

  updateGroupsForPregnant(): void {
    if (!this.specialAssistance?.degreeOfWalking) {
      this.previousGroup = SpecialAssistanceRequestType.SPECIAL_ASSISTANCE;
      return;
    }
    this.previousGroup = this.wheelchairs?.willBeCheckedIn
      ? SpecialAssistanceRequestType.WHEELCHAIR_TYPE
      : SpecialAssistanceRequestType.WHEELCHAIRS;
    this.nextGroup = this.type;
  }

  /**
   * 適用ボタン表示判定
   * @returns 表示可否
   */
  public isSubmit(): boolean {
    this.resize();
    switch (this.type) {
      case SpecialAssistanceRequestType.SPECIAL_ASSISTANCE:
        return !this.specialAssistance?.degreeOfWalking && !this.specialAssistance?.pregnant;
      case SpecialAssistanceRequestType.WHEELCHAIRS:
        return !this.wheelchairs?.willBeCheckedIn && !this.specialAssistance?.pregnant;
      case SpecialAssistanceRequestType.WHEELCHAIR_TYPE:
        return !this.specialAssistance?.pregnant;
      case SpecialAssistanceRequestType.PREGNANT:
        return true;
    }
    return false;
  }

  /**
   * 次へボタン押下処理
   */
  onClickNext(): void {
    if (
      this.walkingAbilityCom?.isError() ||
      this.wheelchairsCom?.isError() ||
      this.foldingTypeCom?.isError() ||
      this.wheelchairTypeCom?.isError() ||
      this.pregnantCom?.isError()
    ) {
      return;
    }
    this.isNext();
    this.type = this.nextGroup;
    this._change.detectChanges();
  }

  // (42) 戻るボタン押下処理　※サポート情報入力モーダル
  onClickBack(): void {
    this.isNext();
    this.type = this.previousGroup;
    this._change.detectChanges();
  }

  /**
   * (43) 適用ボタン押下処理　※サポート情報入力モーダル
   */
  onClickSubmit(): void {
    // (43.1) 現在表示されているグループの入力チェックを行う。
    if (!this.inputCheck()) return;

    // (43.2) 以下条件の全てを満たす場合、エラー(E1768)とし、おからだの不自由な方の相談デスクを案内する。
    if (!this.postInputCheck()) return;

    if (
      !this.specialAssistance ||
      !this.walkingAbility ||
      !this.wheelchairs ||
      !this.foldingType ||
      !this.wheelchairType ||
      !this.pregnant
    ) {
      return;
    }

    // (43.3) サポート情報入力モーダルを閉じる。
    this.close({
      // (43.4.1) 画面.サポート選択グループの項目のいずれかが以下の状態である場合、画面.サポート情報追加ボタンを非表示とし、画面.サポート情報詳細グループを表示する。
      isSupportRegistered:
        // (43.4.1.1) 画面.歩行障がいがチェックあり
        this.specialAssistance.degreeOfWalking ||
        // (43.4.1.2) 画面.視覚障がいがチェックあり
        this.specialAssistance.blind ||
        // (43.4.1.3) 画面.聴覚障がいがチェックあり
        this.specialAssistance.deaf ||
        // (43.4.1.4) 画面.妊娠中がチェックあり
        this.specialAssistance.pregnant,

      // (43.4.2) 当モーダルにて入力した内容を、画面.サポート情報詳細グループのサポート情報へ反映する。
      specialAssistance: this.specialAssistance,
      walkingAbility: this.walkingAbility,
      wheelchairs: this.wheelchairs,
      foldingType: this.foldingType,
      wheelchairType: this.wheelchairType,
      pregnant: this.pregnant,
    } as SpecialAssistanceRequestData);

    // (43.5) 画面.サポート選択グループが全て未選択の場合、画面.サポート情報追加ボタンを表示し、画面.サポート情報詳細グループを非表示とする。
  }

  /**
   * (36) 閉じるボタン押下処理　※サポート情報入力モーダル
   */
  onClickClose(): void {
    this.close();
  }

  /**
   * (43.1) 現在表示されているグループの入力チェックを行う。
   * @return boolean
   */
  inputCheck(): boolean {
    this.specialAssistanceCom?.apply();
    this.walkingAbilityCom?.apply();
    this.wheelchairsCom?.apply();
    this.foldingTypeCom?.apply();
    this.wheelchairTypeCom?.apply();
    this.pregnantCom?.apply();

    const isError =
      this.walkingAbilityCom?.isError() ||
      this.wheelchairsCom?.isError() ||
      this.foldingTypeCom?.isError() ||
      this.wheelchairTypeCom?.isError() ||
      this.pregnantCom?.isError();

    return !isError;
  }

  /**
   * (43.2) 以下条件の全てを満たす場合、エラー(E1768)とし、おからだの不自由な方の相談デスクを案内する。
   * @returns boolean
   */
  postInputCheck(): boolean {
    if (
      // (43.2.1) PNR情報取得API応答.data.air.tripType.domestic（日本国内単独旅程）となる
      this.isDomesticFlight &&
      // (43.2.2) ユーザ共通情報.POS国コード≠”US”(アメリカ)
      !this.isUSPos &&
      // (43.2.3) PNR情報取得APIから取得したPNR情報取得API応答.data.travelers.passengerTypeCode.INS、またはPNR情報取得API応答.data.travelers.passengerTypeCode.INFが存在する
      this.isInfantExisting &&
      // (44.2.4) 画面.妊娠中がチェックあり、または利用サポート情報.サポート情報歩行の程度ののSSRコード = "WCHC"の場合
      ((this.specialAssistance?.degreeOfWalking === true &&
        this.walkingAbility?.code === UpdateBasicInformationSupportRequest.DegreeSpecialServiceRequestEnum.Wchc) ||
        this.specialAssistance?.pregnant)
    ) {
      this.alertMessageItem = {
        alertType: AlertType.WARNING,
        errorMessageId: 'E1768',
        contentHtml: 'E1768',
        isCloseEnable: true,
      };
      this.isError = true;

      return false;
    }

    this.isError = false;

    return true;
  }

  override reload(): void {
    /* override abstract reload function of base class */
  }

  override destroy(): void {
    /* override abstract destroy function of base class */
  }
}
